/**
 * Henley Spirit bespoke product template.
 * - HenleyCart: shared helper that adds one or more variants to the cart via
 *   the native Ajax Cart API (/cart/add.js) and hands the response to
 *   whichever <cart-drawer>/<cart-notification> already exists on the page,
 *   exactly like assets/product-form.js does for the stock buy box.
 * - <henley-product-page>: the buy box on the product template itself
 *   (gallery, variant swatches, quantity, add-ons, add to cart).
 * - Delegated listener for "+ Quick Add" buttons in the Style With rail and
 *   on any other product card sitewide (collection grids, homepage sliders,
 *   the gift set section, etc.) via [data-henley-quick-add].
 */

window.henleyStrings = window.henleyStrings || {
  addToCart: 'Add to Cart',
  soldOut: 'Sold out',
  unavailable: 'Unavailable',
  adding: 'Adding...',
  genericError: 'There was an error while updating your cart. Please try again.'
};

window.HenleyCart = window.HenleyCart || {
  getCartComponent() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  },

  addItems(items, options) {
    options = options || {};
    const cart = this.getCartComponent();
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    const body = { items: items };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }
    config.body = JSON.stringify(body);

    return fetch(`${window.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          document.dispatchEvent(
            new CustomEvent('cart:error', {
              detail: {
                source: 'henley-product',
                errors: response.description,
                message: response.message
              }
            })
          );
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'henley-product',
              errors: response.description,
              message: response.message
            });
          }
          if (options.onError) options.onError(response);
          return response;
        }

        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'henley-product' });
        }

        if (cart) {
          cart.renderContents(response);
        } else {
          window.location = window.routes.cart_url;
        }

        if (options.onSuccess) options.onSuccess(response);
        return response;
      })
      .catch((error) => {
        console.error(error);
        if (options.onError) options.onError(error);
      });
  }
};

(function () {
  if (customElements.get('henley-product-page')) return;

  class HenleyProductPage extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.variants = JSON.parse(this.querySelector('[data-henley-variants]').textContent);
      this.mediaList = JSON.parse(this.querySelector('[data-henley-media]').textContent);
      this.optionCount = this.variants[0] ? this.variants[0].options.length : 0;

      this.form = this.querySelector('[data-henley-form]');
      this.variantIdInput = this.querySelector('[data-henley-variant-id]');
      this.qtyInput = this.querySelector('[data-henley-qty]');
      this.priceEl = this.querySelector('[data-henley-price]');
      this.comparePriceEl = this.querySelector('[data-henley-compare-price]');
      this.availabilityEl = this.querySelector('[data-henley-availability]');
      this.submitButton = this.querySelector('[data-henley-submit]');
      this.errorEl = this.querySelector('[data-henley-error]');
      this.mainImg = this.querySelector('[data-henley-main-img]');
      this.thumbRail = this.querySelector('[data-henley-thumb-rail]');
      this.mobileTrack = this.querySelector('[data-henley-mobile-track]');
      this.mobileDots = this.querySelector('[data-henley-mobile-dots]');
      this.lightbox = this.querySelector('[data-henley-lightbox]');
      this.lightboxImg = this.lightbox ? this.lightbox.querySelector('[data-henley-lb-img]') : null;

      this.selectedOptions = this.variants
        .filter((v) => String(v.id) === String(this.variantIdInput.value))[0];
      this.selectedOptions = this.selectedOptions ? this.selectedOptions.options.slice() : [];

      this.currentMediaIndex = 0;
      this.activeMedia = this.mediaList.slice();

      // Variant Media Grouping: media alt text ending with "#ColorName" belongs to that color
      this.groupOptionPosition =
        this.dataset.altMediaGrouping === 'true' ? parseInt(this.dataset.groupOptionPosition, 10) || 0 : 0;
      this.mediaGroupValues = {};
      const groupValuesEl = this.querySelector('[data-henley-media-group-values]');
      if (groupValuesEl) {
        try {
          this.mediaGroupValues = JSON.parse(groupValuesEl.textContent) || {};
        } catch (error) {
          this.mediaGroupValues = {};
        }
      }

      this.bindOptionButtons();
      this.bindGallery();
      this.bindQuantity();
      this.bindAddons();
      this.bindSubmit();

      this.applyVariantMediaFilter(this.findMatchingVariant());
      this.updateAvailabilityStates();
      this.preloadMedia();
    }

    preloadMedia() {
      // Warms the browser cache for every gallery/variant image up front so
      // clicking a thumbnail or switching variants swaps instantly instead
      // of waiting on a fresh network request.
      this.mediaList.forEach((media) => {
        const img = new Image();
        img.src = media.src;
      });
    }

    bindOptionButtons() {
      this.querySelectorAll('[data-henley-option-value]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const position = parseInt(btn.dataset.henleyOptionPosition, 10) - 1;
          this.selectedOptions[position] = btn.dataset.henleyOptionValue;

          this.querySelectorAll(
            `[data-henley-option-position="${position + 1}"]`
          ).forEach((b) => b.classList.remove('henley-active'));
          btn.classList.add('henley-active');

          this.onSelectionChange();
        });
      });
    }

    findMatchingVariant() {
      return this.variants.filter((variant) => {
        return variant.options.every((value, index) => value === this.selectedOptions[index]);
      })[0];
    }

    onSelectionChange() {
      const variant = this.findMatchingVariant();
      this.updateAvailabilityStates();

      if (!variant) {
        this.priceEl.textContent = '';
        this.comparePriceEl.textContent = '';
        this.availabilityEl.textContent = window.henleyStrings.unavailable;
        this.availabilityEl.classList.add('henley-availability--out');
        this.submitButton.disabled = true;
        this.submitButton.querySelector('.henley-btn-label').textContent = window.henleyStrings.unavailable;
        // The selected color still decides the gallery when the combination doesn't exist
        if (this.groupOptionPosition) this.applyVariantMediaFilter(null);
        return;
      }

      this.variantIdInput.value = variant.id;
      this.priceEl.textContent = variant.priceHtml;

      if (variant.compareAtPriceHtml && variant.onSale) {
        this.comparePriceEl.textContent = variant.compareAtPriceHtml;
        this.comparePriceEl.hidden = false;
      } else {
        this.comparePriceEl.textContent = '';
        this.comparePriceEl.hidden = true;
      }

      if (variant.available) {
        this.availabilityEl.textContent = '';
        this.availabilityEl.classList.remove('henley-availability--out');
        this.submitButton.disabled = false;
        this.submitButton.querySelector('.henley-btn-label').textContent = window.henleyStrings.addToCart;
      } else {
        this.availabilityEl.textContent = window.henleyStrings.soldOut;
        this.availabilityEl.classList.add('henley-availability--out');
        this.submitButton.disabled = true;
        this.submitButton.querySelector('.henley-btn-label').textContent = window.henleyStrings.soldOut;
      }

      this.applyVariantMediaFilter(variant);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    updateAvailabilityStates() {
      for (let position = 0; position < this.optionCount; position++) {
        this.querySelectorAll(`[data-henley-option-position="${position + 1}"]`).forEach((btn) => {
          const candidate = this.selectedOptions.slice();
          candidate[position] = btn.dataset.henleyOptionValue;
          const exists = this.variants.some((variant) => {
            return variant.available && variant.options.every((value, index) => value === candidate[index]);
          });
          btn.classList.toggle('henley-unavailable', !exists);
        });
      }
    }

    applyVariantMediaFilter(variant) {
      const variantMediaIds = this.variants
        .map((v) => v.featuredMediaId)
        .filter(Boolean);

      const selectedGroup = this.getSelectedMediaGroup();

      let media = this.mediaList.filter((m) => {
        if (variant && m.id === variant.featuredMediaId) return true;
        // Tagged media follows its color group, untagged media keeps the default rule
        if (selectedGroup && m.group) return m.group === selectedGroup;
        return !variantMediaIds.includes(m.id);
      });

      if (variant && variant.featuredMediaId) {
        const featured = this.mediaList.filter((m) => m.id === variant.featuredMediaId)[0];
        if (featured) {
          media = [featured].concat(media.filter((m) => m.id !== featured.id));
        }
      }

      if (!media.length) media = this.mediaList.slice();

      this.activeMedia = media;
      this.renderGallery();
    }

    getSelectedMediaGroup() {
      if (!this.groupOptionPosition) return '';

      const value = this.selectedOptions[this.groupOptionPosition - 1];
      if (value === undefined || value === null) return '';

      if (Object.prototype.hasOwnProperty.call(this.mediaGroupValues, value)) {
        return this.mediaGroupValues[value];
      }

      // Fallback only: exact handles are rendered by Liquid in [data-henley-media-group-values]
      return String(value)
        .toLowerCase()
        .trim()
        .replace(/['"]/g, '')
        .replace(/[^\p{L}\p{N}]+/gu, '-')
        .replace(/^-+|-+$/g, '');
    }

    renderGallery() {
      if (this.thumbRail) {
        this.thumbRail.innerHTML = '';
        this.activeMedia.forEach((media, index) => {
          const thumb = document.createElement('button');
          thumb.type = 'button';
          thumb.className = 'henley-thumb' + (index === 0 ? ' henley-active' : '');
          thumb.setAttribute('aria-label', media.alt || '');
          thumb.dataset.henleyThumbIndex = index;
          const img = document.createElement('img');
          img.src = media.thumb;
          img.alt = media.alt || '';
          img.loading = 'lazy';
          thumb.appendChild(img);
          thumb.addEventListener('click', () => this.setActiveMedia(index));
          this.thumbRail.appendChild(thumb);
        });
      }
      this.renderMobileGallery();
      this.setActiveMedia(0);
    }

    renderMobileGallery() {
      if (!this.mobileTrack || !this.mobileDots) return;

      this.mobileTrack.innerHTML = '';
      this.mobileDots.innerHTML = '';

      this.activeMedia.forEach((media, index) => {
        const slide = document.createElement('div');
        slide.className = 'henley-mobile-slide';
        slide.dataset.henleyMobileSlideIndex = index;
        const img = document.createElement('img');
        img.src = media.src;
        img.alt = media.alt || '';
        img.loading = index === 0 ? 'eager' : 'lazy';
        slide.appendChild(img);
        slide.addEventListener('click', () => {
          this.currentMediaIndex = index;
          this.openLightbox();
        });
        this.mobileTrack.appendChild(slide);

        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'henley-mobile-dot' + (index === 0 ? ' henley-active' : '');
        dot.dataset.henleyMobileDotIndex = index;
        dot.setAttribute('aria-label', media.alt || '');
        dot.addEventListener('click', () => {
          this.mobileTrack.scrollTo({ left: index * this.mobileTrack.clientWidth, behavior: 'smooth' });
        });
        this.mobileDots.appendChild(dot);
      });

      this.mobileTrack.scrollLeft = 0;
    }

    setActiveMedia(index) {
      if (!this.activeMedia[index]) return;
      this.currentMediaIndex = index;
      this.mainImg.src = this.activeMedia[index].src;
      this.mainImg.alt = this.activeMedia[index].alt || '';
      if (this.thumbRail) {
        this.thumbRail.querySelectorAll('[data-henley-thumb-index]').forEach((t, i) => {
          t.classList.toggle('henley-active', i === index);
        });
      }
    }

    bindGallery() {
      const openBtn = this.querySelector('[data-henley-open-lightbox]');
      if (openBtn) openBtn.addEventListener('click', () => this.openLightbox());

      if (this.mobileTrack) {
        let scrollTimeout;
        this.mobileTrack.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            const width = this.mobileTrack.clientWidth || 1;
            const index = Math.round(this.mobileTrack.scrollLeft / width);
            if (!this.mobileDots) return;
            this.mobileDots.querySelectorAll('[data-henley-mobile-dot-index]').forEach((dot, i) => {
              dot.classList.toggle('henley-active', i === index);
            });
          }, 100);
        });
      }

      if (!this.lightbox) return;
      this.querySelector('[data-henley-lb-close]').addEventListener('click', () => this.closeLightbox());
      this.querySelector('[data-henley-lb-prev]').addEventListener('click', () => this.navLightbox(-1));
      this.querySelector('[data-henley-lb-next]').addEventListener('click', () => this.navLightbox(1));
      this.bindLightboxSwipe();
      this.lightbox.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.closeLightbox();
      });
      document.addEventListener('keydown', (event) => {
        if (!this.lightbox.classList.contains('henley-open')) return;
        if (event.key === 'Escape') this.closeLightbox();
        if (event.key === 'ArrowLeft') this.navLightbox(-1);
        if (event.key === 'ArrowRight') this.navLightbox(1);
      });
    }

    bindLightboxSwipe() {
      if (!this.lightbox || !this.lightboxImg) return;

      let startX = 0;
      let startY = 0;
      let tracking = false;

      const start = (event) => {
        const point = event.touches ? event.touches[0] : event;
        if (!point) return;
        startX = point.clientX;
        startY = point.clientY;
        tracking = true;
      };

      const end = (event) => {
        if (!tracking) return;
        const point = event.changedTouches ? event.changedTouches[0] : event;
        if (!point) return;

        const deltaX = point.clientX - startX;
        const deltaY = point.clientY - startY;
        tracking = false;

        if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;
        this.navLightbox(deltaX < 0 ? 1 : -1);
      };

      this.lightboxImg.addEventListener('touchstart', start, { passive: true });
      this.lightboxImg.addEventListener('touchend', end);
      this.lightboxImg.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        start(event);
      });
      this.lightboxImg.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse') return;
        end(event);
      });
    }

    openLightbox() {
      if (!this.lightbox || !this.activeMedia.length) return;
      this.lightboxImg.src = this.activeMedia[this.currentMediaIndex].src;
      this.lightbox.classList.add('henley-open');
      document.body.style.overflow = 'hidden';
    }

    closeLightbox() {
      this.lightbox.classList.remove('henley-open');
      document.body.style.overflow = '';
    }

    navLightbox(direction) {
      const total = this.activeMedia.length;
      this.currentMediaIndex = (this.currentMediaIndex + direction + total) % total;
      this.lightboxImg.src = this.activeMedia[this.currentMediaIndex].src;
      this.setActiveMedia(this.currentMediaIndex);
    }

    bindQuantity() {
      this.querySelectorAll('[data-henley-qty-btn]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const delta = parseInt(btn.dataset.henleyQtyBtn, 10);
          this.qtyInput.value = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + delta);
        });
      });
    }

    bindAddons() {
      this.querySelectorAll('[data-henley-addon]').forEach((item) => {
        item.addEventListener('click', () => {
          item.classList.toggle('henley-selected');
        });
      });
    }

    getSelectedAddonItems() {
      return Array.from(this.querySelectorAll('[data-henley-addon].henley-selected')).map((item) => ({
        id: parseInt(item.dataset.henleyAddon, 10),
        quantity: 1
      }));
    }

    bindSubmit() {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (this.submitButton.disabled) return;

        this.submitButton.classList.add('henley-loading');
        this.submitButton.disabled = true;
        if (this.errorEl) this.errorEl.hidden = true;

        const items = [
          { id: parseInt(this.variantIdInput.value, 10), quantity: parseInt(this.qtyInput.value, 10) || 1 }
        ].concat(this.getSelectedAddonItems());

        window.HenleyCart.addItems(items, {
          onError: (response) => {
            if (this.errorEl) {
              this.errorEl.textContent = (response && response.message) || window.henleyStrings.genericError;
              this.errorEl.hidden = false;
            }
          },
          onSuccess: () => {}
        }).finally(() => {
          this.submitButton.classList.remove('henley-loading');
          this.submitButton.disabled = false;
        });
      });
    }
  }

  customElements.define('henley-product-page', HenleyProductPage);
})();

// Guarded at the top level (not inside a customElements check) because this
// script tag is now included from many places on the same page (product
// cards render it once per card) — without this guard the listener would
// be attached once per inclusion and fire the same add-to-cart multiple
// times per click.
if (!window.henleyQuickAddBound) {
  window.henleyQuickAddBound = true;

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-henley-quick-add]');
    if (!btn || btn.disabled) return;
    event.preventDefault();

    const variantId = parseInt(btn.dataset.henleyQuickAdd, 10);
    if (!variantId) return;

    btn.disabled = true;
    btn.classList.add('henley-loading');

    window.HenleyCart.addItems([{ id: variantId, quantity: 1 }]).finally(() => {
      btn.disabled = false;
      btn.classList.remove('henley-loading');
    });
  });
}
